import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { plannerAPI } from "../../api/client";
import { formatLocalDate, parseLocalDate } from "../../utils/dateUtils";
import HourlyCalendar from "./HourlyCalendar";
import PlannerContextMenu from "./PlannerContextMenu";
import RecurringScheduleConfirmModal from "./RecurringScheduleConfirmModal";

export default function PlannerDayView({
  focusDate,
  data,
  isAdmin,
  onRefresh,
}) {
  const navigate = useNavigate();
  const dateStr = useMemo(() => {
    if (focusDate instanceof Date) return formatLocalDate(focusDate);
    return formatLocalDate(parseLocalDate(focusDate));
  }, [focusDate]);
  const dayData = data?.days?.[dateStr];
  const [tasks, setTasks] = useState(dayData?.tasks || []);
  const [toastMessage, setToastMessage] = useState("");
  const toastTimerRef = useRef(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);

  useEffect(() => {
    setTasks(dayData?.tasks || []);
  }, [dateStr, dayData]);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
    };
  }, []);

  const showToast = (message) => {
    setToastMessage(message);
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = window.setTimeout(() => {
      setToastMessage("");
      toastTimerRef.current = null;
    }, 2500);
  };

  const handleReorder = async (reordered) => {
    if (!data?.user?.id) return;
    const previous = tasks;
    setTasks(
      reordered.map((item) => {
        const existing = tasks.find((t) => t.id === item.id);
        return existing
          ? {
              ...existing,
              scheduledStartTime: item.scheduledStartTime,
              isOvertime: item.isOvertime,
              queuePosition: item.queuePosition,
            }
          : existing;
      }),
    );
    try {
      await plannerAPI.reorder({
        userId: data.user.id,
        assignments: reordered,
      });
      onRefresh();
    } catch (err) {
      setTasks(previous);
      showToast("Failed to reschedule. Please try again.");
    }
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    if (!dayData) return;
    setContextMenu({
      x: Math.min(e.clientX, window.innerWidth - 220),
      y: Math.min(e.clientY, window.innerHeight - 300),
      dateStr,
      dayInfo: dayData,
      user: data?.user,
    });
  };

  const handleContextAction = async (action, { dateStr: targetDate, note }) => {
    const userId = data?.user?.id;

    switch (action) {
      case "MARK_LEAVE":
        if (userId) {
          await plannerAPI.upsertDayOverride(userId, {
            date: targetDate,
            type: "OFF",
            note: String(note || "").trim() || "Leave",
          });
          await onRefresh();
        }
        break;
      case "MARK_HALF_DAY":
        if (userId) {
          await plannerAPI.upsertDayOverride(userId, {
            date: targetDate,
            type: "HALF_DAY",
          });
          await onRefresh();
        }
        break;
      case "MARK_ONE_OFF_WORKING":
        if (userId) {
          await plannerAPI.upsertDayOverride(userId, {
            date: targetDate,
            type: "WORKING",
            note: "One-off working day",
          });
          await onRefresh();
        }
        break;
      case "RESTORE_DAY":
      case "REMOVE_OVERRIDE":
        if (userId) {
          await plannerAPI.removeDayOverride(userId, targetDate);
          await onRefresh();
        }
        break;
      case "MARK_RECURRING_OFF":
        if (data?.user) {
          setConfirmModal({
            type: "REMOVE_WORKING_DAY",
            dateStr: targetDate,
            user: data.user,
            currentWorkingDays: data.workingDays || [],
          });
        }
        break;
      case "VIEW_TASKS":
        break;
      default:
        break;
    }

    setContextMenu(null);
  };

  const dateObj = parseLocalDate(dateStr) || new Date();
  const formattedDate = dateObj.toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="day-view">
      <div className="day-view-header" onContextMenu={handleContextMenu}>
        <h3>{formattedDate}</h3>
        {data?.user && (
          <span className="day-view-user">
            {data.user.firstName} {data.user.lastName}
          </span>
        )}
        {data?.workStartTime && (
          <span className="day-view-hours">
            Hours: {data.workStartTime} - {data.workEndTime}
          </span>
        )}
      </div>

      {toastMessage && <div className="planner-toast">{toastMessage}</div>}

      {dayData?.type === "HOLIDAY" && (
        <div className="day-status-banner holiday">
          Public Holiday ? {dayData.holidayName}
        </div>
      )}

      {dayData?.type === "WEEKLY_OFF" && (
        <div className="day-status-banner weekly-off">Weekly Off</div>
      )}

      {dayData?.type === "OFF" && (
        <div className="day-status-banner weekly-off">{dayData?.overrideNote || "Leave"}</div>
      )}

      {dayData?.type === "HALF_DAY" && (
        <div className="day-status-banner weekly-off">Half Day</div>
      )}

      {dayData?.type === "WORKING" && (
        <>
          {tasks.length === 0 ? (
            <p className="day-empty">No tasks assigned for this day</p>
          ) : (
            <HourlyCalendar
              tasks={tasks}
              workStartTime={data?.workStartTime || "09:00"}
              workEndTime={data?.workEndTime || "18:00"}
              workflowConnections={tasks.flatMap(
                (task) => task.workflowConnections || [],
              )}
              isAdmin={isAdmin}
              onReorder={handleReorder}
              onTaskClick={(task) =>
                navigate(`/projects/${task.projectId}/stage/${task.id}`)
              }
            />
          )}
        </>
      )}

      {contextMenu && (
        <PlannerContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          dateStr={contextMenu.dateStr}
          dayInfo={contextMenu.dayInfo}
          user={contextMenu.user}
          isAdmin={isAdmin}
          onAction={handleContextAction}
          onClose={() => setContextMenu(null)}
        />
      )}

      {confirmModal && data?.user && (
        <RecurringScheduleConfirmModal
          user={confirmModal.user}
          dateStr={confirmModal.dateStr}
          action={confirmModal.type}
          currentWorkingDays={confirmModal.currentWorkingDays}
          onConfirm={async ({ newWorkingDays, effectiveFrom }) => {
            await plannerAPI.updateUserSchedule(confirmModal.user.id, {
              workingDays: newWorkingDays,
              workStartTime: data?.workStartTime,
              workEndTime: data?.workEndTime,
              effectiveFrom,
            });
            setConfirmModal(null);
            onRefresh();
          }}
          onClose={() => setConfirmModal(null)}
        />
      )}
    </div>
  );
}

